import { useHistory, useRouteMatch } from 'react-router';
import { useEffect, useReducer } from 'react';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useSelector } from 'react-redux';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useAutomationStats } from '../AutomationHome/use-automation-stats';
import { getErrorJson } from '@mm/utils';
import { useDisabledInputTriggers } from './InputTriggers/use-disabled-input-triggers';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { ALREADY_USED_TRIGGER_ERROR_TEXT } from '@mm/state/errorHandlers/campaigns-handler';
import { camelize } from '@mm/state/helpers/converterString';
import { errorToast } from '../../../../../../common/helpers/toast';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useAutomationsCRUDActions } from '@mm/state/actions/automationActions';

type BusyActions =
  | ''
  | 'fetching'
  | 'updating'
  | 'editing'
  | 'drawing'
  | 'stats';

type AutomationType = {
  name?: string;
};

export type AutomationState = {
  busy: BusyActions;
  source?: CampaignType; // non mutated original
  automation?: AutomationType;
  error: string;
};

enum AutomationActionType {
  fetch = 'fetch',
  fetchSuccess = 'fetchSuccess',
  fetchError = 'fetchError',
  setName = 'setName',
  start = 'start',
  startSuccess = 'startSuccess',
  update = 'update',
  updateSuccess = 'updateSuccess',
  pause = 'pause',
  pauseSuccess = 'pauseSuccess',
  busy = 'busy',
  fetchStats = 'fetchStats',
  fetchStatsSuccess = 'fetchStatsSuccess',
  fetchStatsError = 'fetchStatsError',
  delete = 'delete',
}

type AutomationAction =
  | {
      type:
        | AutomationActionType.fetchStats
        | AutomationActionType.fetchStatsSuccess
        | AutomationActionType.fetchStatsError;
    }
  | {
      type: AutomationActionType.fetch;
      payload: number;
    }
  | {
      type: AutomationActionType.fetchSuccess;
      payload: CampaignType;
    }
  | {
      type: AutomationActionType.fetchError;
      payload: string;
    }
  | {
      type: AutomationActionType.setName;
      payload: string;
    }
  | {
      type: AutomationActionType.start;
    }
  | {
      type: AutomationActionType.pause;
    }
  | {
      type: AutomationActionType.startSuccess;
      payload: CampaignType;
    }
  | {
      type: AutomationActionType.pauseSuccess;
      payload: CampaignType;
    }
  | {
      type: AutomationActionType.busy;
      payload: BusyActions;
    }
  | {
      type: AutomationActionType.update;
    }
  | {
      type: AutomationActionType.updateSuccess;
      payload: CampaignType;
    }
  | {
      type: AutomationActionType.delete;
    };

const initialStateDefaults = {
  busy: '',
  source: undefined,
  automation: undefined,
  error: '',
};

const reducer = (state: AutomationState, action: AutomationAction) => {
  switch (action.type) {
    case AutomationActionType.fetch:
      return {
        ...state,
        busy: 'fetching',
      };
    case AutomationActionType.fetchSuccess:
      return {
        ...state,
        busy: '',
        source: action.payload,
        automation: action.payload,
      };
    case AutomationActionType.fetchError:
      return {
        ...state,
        busy: '',
        error: action.payload,
      };
    case AutomationActionType.setName:
      return {
        ...state,
        automation: { ...state.automation, name: action.payload },
      };

    case AutomationActionType.start:
    case AutomationActionType.pause:
    case AutomationActionType.update:
      return {
        ...state,
        busy: 'updating',
      };
    case AutomationActionType.delete:
      return {
        ...state,
        busy: 'deleting',
      };

    case AutomationActionType.startSuccess:
    case AutomationActionType.pauseSuccess:
    case AutomationActionType.updateSuccess:
      return {
        ...state,
        busy: '',
        source: action.payload,
        automation: action.payload,
      };

    case AutomationActionType.fetchStats:
      return {
        ...state,
        busy: 'stats',
      };

    case AutomationActionType.fetchStatsSuccess:
    case AutomationActionType.fetchStatsError:
      return {
        ...state,
        busy: '',
      };

    case AutomationActionType.busy:
      return {
        ...state,
        busy: action.payload,
      };

    default:
      return {
        ...state,
      };
  }
};

export const useAutomation = ({
  id,
  initialState,
}: {
  id: number;
  initialState?: AutomationState;
}) => {
  const actions = useCampaignCRUDActions();
  const botId = useSelector(activeChatbotIdSelector);
  const { fetchStats } = useAutomationStats();
  const automationCRUDActions = useAutomationsCRUDActions();
  const match = useRouteMatch<{ id?: string; chatbotId: string }>();
  const history = useHistory();
  const { openGeneralConfirmModal } = useModalActions();

  const modalActions = useModalActions();

  const disabledInputTriggers = useDisabledInputTriggers({
    onLinkClick: () => modalActions.closeModal(),
  });

  const [state, dispatch] = useReducer(
    reducer,
    initialState || initialStateDefaults,
  );

  const fetchAutomation = async () => {
    if ((id && !state.source) || id !== state.source?.id) {
      dispatch({ type: AutomationActionType.fetch, payload: id });
      try {
        const payload = await actions.retrieve(id);
        if (payload) {
          dispatch({ type: AutomationActionType.fetchSuccess, payload });
        }
      } catch (err) {
        dispatch({ type: AutomationActionType.fetchError, payload: err });
      }
    }
  };

  const start = async () => {
    dispatch({ type: AutomationActionType.start });

    try {
      const res = await actions.updateCampaign(
        {
          id,
          active: true,
          status: 'queued',
        } as CampaignType,
        botId,
      );
      const payload = res.campaign;

      if (payload) {
        dispatch({ type: AutomationActionType.startSuccess, payload });
      }

      return payload;
    } catch (err) {
      if (err.status === 422) {
        const json: { campaign?: CampaignType; errors: string[] } = camelize(
          await getErrorJson(err),
        );
        const { errors, campaign } = json || {
          errors: [],
          campaign: undefined,
        };
        // already used trigger error
        const alreadyUsedTriggerError =
          Array.isArray(errors) &&
          errors.length &&
          errors[0].toString().includes(ALREADY_USED_TRIGGER_ERROR_TEXT);

        if (
          alreadyUsedTriggerError &&
          campaign &&
          campaign.campaignAudiencesAttributes
        ) {
          const ids = campaign.campaignAudiencesAttributes
            .map(attr => attr.usedInAutomationId)
            .filter(Boolean) as number[];

          const toRender = disabledInputTriggers.template(
            '',
            [ids[0]],
            'This trigger is already in use in ',
            'other active automation',
          );

          modalActions.openGeneralTextModal({
            header: 'Ooops!',
            message: toRender,
            closeButtonText: 'Close',
          });
        }
      }
    }
  };

  const update = async (update: CampaignType) => {
    dispatch({ type: AutomationActionType.update });

    try {
      const res = await actions.updateCampaign(update, botId);
      const payload = res.campaign;

      if (payload) {
        dispatch({ type: AutomationActionType.updateSuccess, payload });
      }
    } catch (err) {}
  };

  const pause = async () => {
    dispatch({ type: AutomationActionType.pause });

    try {
      const res = await actions.updateCampaign(
        {
          id,
          status: 'paused',
        } as CampaignType,
        botId,
      );
      const payload = res.campaign;

      if (payload) {
        dispatch({ type: AutomationActionType.pauseSuccess, payload });
      }
    } catch (err) {}
  };

  const getStats = async () => {
    dispatch({ type: AutomationActionType.fetchStats });

    try {
      await fetchStats([id.toString()]);
      dispatch({ type: AutomationActionType.fetchStatsSuccess });
    } catch (err) {
      dispatch({ type: AutomationActionType.fetchStatsError });
    }
  };

  const deleteAutomation = ({
    skipPush,
    onSuccess,
  }: {
    skipPush?: boolean;
    onSuccess?: () => void;
  }) => {
    dispatch({ type: AutomationActionType.delete });

    const deleteCallback = async ok => {
      if (ok && id) {
        try {
          await automationCRUDActions.deleteAutomation({
            id,
            destroyAll: true,
          });
          onSuccess?.();

          if (!skipPush) {
            history.push(
              BotBuilderRoutes.automations.getLink({
                chatbotId: parseInt(match.params.chatbotId, 10),
                view: 'my',
              }),
            );
          }
        } catch (err) {
          errorToast('Something went wrong. Please try again later');
          console.error(err);
        }
      }
    };

    const headerText = 'Are you sure you want to delete this Automation?';
    const messageText = 'This action cannot be undone';

    openGeneralConfirmModal({
      onCloseHandler: deleteCallback,
      headerText,
      messageText,
      okButtonText: 'Delete Permanently',
      okButtonStyling: { red: true },
    });
  };

  useEffect(() => {
    fetchAutomation();
    fetchStats([id.toString()]);
  }, [id]);

  return {
    state,
    actions: {
      setName(payload: string) {
        dispatch({ type: AutomationActionType.setName, payload });
      },
      busy(payload: BusyActions) {
        dispatch({ type: AutomationActionType.busy, payload });
      },
      saveName() {
        update({ id, name: state.automation?.name } as CampaignType);
      },
      removeNeedsReview() {
        update({ id, needsReview: false } as CampaignType);
      },
      getStats,
      start,
      pause,
      fetchAutomation,
      deleteAutomation,
    },
  };
};
